import {
  Apple,
  Bell,
  CalendarDays,
  Dumbbell,
  LayoutDashboard,
  LineChart,
  MapPin,
  Settings,
  Ticket,
  type LucideIcon,
  UserRoundSearch,
  Wallet,
} from "lucide-react";
import type { ComponentType } from "react";

import { BookingsNavBadge } from "@/features/booking/modules/client-bookings/components/bookings-nav-badge";
import { UnreadNavBadge } from "@/features/notifications/modules/unread-nav-badge/unread-nav-badge";
import { m } from "@/paraglide/messages.js";

/* -------------------------------------------------------------------------------------------------
 * Types
 * -----------------------------------------------------------------------------------------------*/

export type NavPermission = {
  action: string;
  subject: string;
};

export type NavItem = {
  label: string;
  icon: LucideIcon;
  to: string;
  params?: Record<string, string>;
  permission?: NavPermission;
  adminRole?: string;
};

export type NavSection = {
  label: string;
  icon: LucideIcon;
  /** A live count shown on the pill (unread notifications, requests waiting). */
  badge?: ComponentType;
  items: NavItem[];
  permission?: NavPermission;
  adminRole?: string;
};

export type NavSectionLink = NavSection & {
  to: string;
  params?: Record<string, string>;
};

export type NavEntry = NavSection | NavSectionLink;

// Section headers shown above each nav group. Empty by default so every layout
// falls back to its own built-in label; the admin shell overrides these so its
// sidebar reads the same across whichever layout it mounts.
export type NavGroupLabels = {
  primary?: string;
  find?: string;
  secondary?: string;
  tertiary?: string;
  settings?: string;
};

/* -------------------------------------------------------------------------------------------------
 * Navigation
 * -----------------------------------------------------------------------------------------------*/

export const navGroupLabels: NavGroupLabels = { find: m.find_nav_group() };

/** The main menu: the dashboard, then everything that is this person's own - plans, diet, progress. */
export const primaryNav: NavEntry[] = [
  {
    label: m.layout_nav_dashboard(),
    icon: LayoutDashboard,
    to: "/dashboard",
    items: [],
  },
  {
    label: m.hub_nav(),
    icon: Dumbbell,
    to: "/training",
    items: [],
  },
  {
    label: m.hub_nav_diet(),
    icon: Apple,
    to: "/diet",
    items: [],
  },
  {
    label: m.my_progress_title(),
    icon: LineChart,
    to: "/progress",
    items: [],
  },
];

/** The calendar of what is booked, then what there is to book on the platform: everyone on one page, events, gyms. */
export const findNav: NavEntry[] = [
  {
    label: m.booking_portal_nav_bookings(),
    icon: CalendarDays,
    to: "/my-bookings",
    badge: BookingsNavBadge,
    items: [],
  },
  {
    label: m.find_specialists(),
    icon: UserRoundSearch,
    to: "/book",
    items: [],
  },
  {
    label: m.booking_portal_tab_events(),
    icon: Ticket,
    to: "/events",
    items: [],
  },
  {
    label: m.gyms_title(),
    icon: MapPin,
    to: "/gyms",
    items: [],
  },
];

export const tertiaryNav: NavEntry[] = [
  {
    label: m.notifications_nav_label(),
    icon: Bell,
    to: "/notifications",
    badge: UnreadNavBadge,
    items: [],
  },
  {
    label: m.my_payments_title(),
    icon: Wallet,
    to: "/my-payments",
    items: [],
  },
  {
    label: m.settings_nav_label(),
    icon: Settings,
    to: "/settings",
    items: [],
  },
];

export const settingsNav: NavEntry[] = [];
